// Lightbox — fullscreen image viewer, triggered from AttachmentGrid thumbnails.
// Esc / click-backdrop / click × to dismiss. Arrow keys cycle through the
// current bubble's attachments.

import { useEffect, useState } from 'react';

export interface LightboxImage {
  url: string;
  alt: string;
}

interface LightboxProps {
  images: LightboxImage[];
  initialIndex: number;
  onClose: () => void;
}

export function Lightbox({ images, initialIndex, onClose }: LightboxProps) {
  const [idx, setIdx] = useState(initialIndex);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIdx((i) => (i + 1) % images.length);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIdx((i) => (i - 1 + images.length) % images.length);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [images.length, onClose]);

  const current = images[idx];
  if (!current) return null;

  return (
    <div
      className="cc-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Image ${idx + 1} of ${images.length}`}
      onClick={onClose}
    >
      <button
        type="button"
        className="cc-lightbox-close"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        ×
      </button>
      {images.length > 1 ? (
        <>
          <button
            type="button"
            className="cc-lightbox-nav prev"
            aria-label="Previous image"
            onClick={(e) => {
              e.stopPropagation();
              setIdx((i) => (i - 1 + images.length) % images.length);
            }}
          >
            ‹
          </button>
          <button
            type="button"
            className="cc-lightbox-nav next"
            aria-label="Next image"
            onClick={(e) => {
              e.stopPropagation();
              setIdx((i) => (i + 1) % images.length);
            }}
          >
            ›
          </button>
        </>
      ) : null}
      <img
        src={current.url}
        alt={current.alt}
        onClick={(e) => e.stopPropagation()}
      />
      {images.length > 1 ? (
        <div className="cc-lightbox-counter">
          {idx + 1} / {images.length}
        </div>
      ) : null}
    </div>
  );
}
